
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import UserNav from '@/components/user/widget/UserNav.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { IAuditLog } from '@/store/modules/userModule';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';

const userModule = namespace('userModule');

@Component({
  name: 'UserAuditLogs',
  components: { UserNav, PaginationBar },
})
export default class UserAuditLogs extends Vue {
  @Prop()
  protected userId!: number;

  @userModule.Getter('auditLogs')
  protected auditLogs!: IAuditLog[];

  @userModule.Action('fetchAuditLogs')
  private fetchAuditLogs!: (payload: any) => Promise<any>;

  @userModule.Action('clearAuditLogs')
  private clearAuditLogs!: () => void;

  protected loading: boolean = false;
  protected expanded: any[] = [];
  protected currentPage: number = 1;
  protected lastPage: number = 1;
  protected total: number = 0;
  protected perPage: number = 10;

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('users.auditLogs.fields.date') as string, value: 'created_at', sortable: false },
      { text: this.$t('users.auditLogs.fields.action') as string, value: 'action', sortable: false },
      { text: this.$t('users.auditLogs.fields.changedFields') as string, value: 'changed_fields', sortable: false },
      { text: this.$t('users.auditLogs.fields.changedBy') as string, value: 'user_id', sortable: false },
      { text: this.$t('users.auditLogs.fields.ip') as string, value: 'ip', sortable: false },
      { text: '', value: 'data-table-expand' },
    ];
  }

  created() {
    this.loadData();
  }

  beforeDestroy() {
    this.clearAuditLogs();
  }

  private async loadData() {
    this.loading = true;
    try {
      const payload = await this.fetchAuditLogs({
        userId: this.userId,
        params: { page: this.currentPage, per_page: this.perPage },
      });
      if (payload) {
        this.total = payload.total || 0;
        this.lastPage = Math.max(1, Math.ceil(this.total / this.perPage));
      }
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.expanded = [];
    this.loadData();
  }

  onPerPageChange(perPage: number) {
    this.perPage = perPage;
    this.currentPage = 1;
    this.expanded = [];
    this.loadData();
  }

  actionColor(action: string): string {
    if (action === 'insert') return 'green darken-1';
    if (action === 'delete') return 'red darken-1';
    return 'blue darken-1';
  }

  changedFields(item: IAuditLog): string {
    const keys = Object.keys(item.new_data || {});
    if (!keys.length) return '—';
    return keys.join(', ');
  }

  diffFields(item: IAuditLog): Array<{ key: string; oldVal: any; newVal: any; isNew: boolean }> {
    const newData = item.new_data || {};
    const oldData = item.old_data || {};
    return Object.keys(newData).map(key => ({
      key,
      oldVal: oldData[key] !== undefined ? oldData[key] : null,
      newVal: newData[key],
      isNew: oldData[key] === undefined,
    }));
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return '(vacío)';
    if (typeof val === 'string' && val === '') return '(vacío)';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  }
}
